import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import { Box, Inline, Pressable, Text } from '@atlaskit/primitives/compiled';
import { cssMap, cx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	label: {
		id: 'performance-insights.load-times-tab.time-range-selector.label.non-final',
		defaultMessage: 'Time range',
		description:
			'Label for the time range selector on the Load times tab of the Performance insights page. The page is only viewable by admins.',
	},
	last7Days: {
		id: 'performance-insights.load-times-tab.time-range-selector.last-7-days.non-final',
		defaultMessage: 'Last 7 days',
		description:
			'Option for the time range selector on the Load times tab of the Performance insights page. Selects the last 7 days of data.',
	},
	last4Weeks: {
		id: 'performance-insights.load-times-tab.time-range-selector.last-4-weeks.non-final',
		defaultMessage: 'Last 4 weeks',
		description:
			'Option for the time range selector on the Load times tab of the Performance insights page. Selects the last 4 weeks of data.',
	},
	last12Weeks: {
		id: 'performance-insights.load-times-tab.time-range-selector.last-12-weeks.non-final',
		defaultMessage: 'Last 12 weeks',
		description:
			'Option for the time range selector on the Load times tab of the Performance insights page. Selects the last 12 weeks of data.',
	},
});

export type TimeRange = '7d' | '4w' | '12w';

const styles = cssMap({
	container: {
		borderColor: token('color.border'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderRadius: token('radius.small'),
		paddingInline: token('space.025'),
		gap: token('space.025'),
		display: 'inline-flex',
		alignItems: 'center',
		height: '32px',
	},
	button: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: 'transparent',
		borderRadius: token('radius.xsmall'),
		color: token('color.text.subtle'),
		height: '26px',
		fontWeight: token('font.weight.medium'),
		paddingBlock: token('space.0'),
		paddingInline: token('space.150'),
	},
	buttonHovered: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
		},
	},
	buttonSelected: {
		backgroundColor: token('color.background.selected'),
		borderWidth: token('border.width'),
		borderColor: token('color.border.selected'),
		borderStyle: 'solid',
		color: token('color.text.selected'),
		cursor: 'default',
	},
});

type TimeRangeButtonProps = {
	id: TimeRange;
	label: string;
	isSelected: boolean;
	onChange: (range: TimeRange) => void;
};

const TimeRangeButton = ({ id, label, isSelected, onChange }: TimeRangeButtonProps) => {
	const handleClick = () => {
		if (!isSelected) onChange(id);
	};

	return (
		<Pressable
			aria-pressed={isSelected}
			xcss={cx(styles.button, isSelected ? styles.buttonSelected : styles.buttonHovered)}
			onClick={handleClick}
		>
			{label}
		</Pressable>
	);
};

type TimeRangeSelectorProps = {
	selectedRange: TimeRange;
	onChange: (range: TimeRange) => void;
};

export const TimeRangeSelector = ({ selectedRange, onChange }: TimeRangeSelectorProps) => {
	const { formatMessage } = useIntl();

	const options = useMemo(
		() => [
			{ id: '7d' as TimeRange, label: formatMessage(i18n.last7Days) },
			{ id: '4w' as TimeRange, label: formatMessage(i18n.last4Weeks) },
			{ id: '12w' as TimeRange, label: formatMessage(i18n.last12Weeks) },
		],
		[formatMessage],
	);

	return (
		<Inline space="space.150" alignBlock="center">
			<Text weight="medium" color="color.text">
				{formatMessage(i18n.label)}
			</Text>
			<Box role="group" aria-label={formatMessage(i18n.label)} xcss={styles.container}>
				{options.map((option) => (
					<TimeRangeButton
						key={option.id}
						id={option.id}
						label={option.label}
						isSelected={option.id === selectedRange}
						onChange={onChange}
					/>
				))}
			</Box>
		</Inline>
	);
};
